<template>
  <b-card no-body>
    <b-card-body id="consignment-card-body">
      <div class="d-flex align-items-center mb-2 justify-content-end">
        <b-button
          variant="primary"
          class="mr-1 d-flex align-items-center"
          @click="openCustomColumnsModal"
        >
          <feather-icon icon="ColumnsIcon" size="18" class="mr-1" />
          <span>{{ t('Columns') }}</span>
        </b-button>
        <b-button
          v-if="isCustomer"
          variant="outline-secondary"
          class="d-flex align-items-center px-2 py-1"
          @click="openExportFromToolbar"
        >
          <feather-icon icon="DownloadIcon" size="18" class="mr-1" />
          <span>{{ t('Customer Export') }}</span>
        </b-button>
        <b-button
          v-if="isAdmin"
          variant="outline-primary"
          class="d-flex align-items-center px-2 py-1 ml-1"
          @click="openAdminExportFromToolbar"
        >
          <feather-icon icon="DownloadIcon" size="18" class="mr-1" />
          <span>{{ t('Admin Export') }}</span>
        </b-button>
      </div>
      <consignment-filter
        ref="consignmentFilter"
        :values="filterValues"
        :filter-submitting="isLoading"
        :export-mode="exportMode"
        @filter="setFilter"
        @export-done="exportMode = null"
      />
      <div class="mt-1 mb-1">
        <div>
          <p>
            {{ $t('Bulk courier will be called for count shipments', { count: selected.length }) }}
          </p>
          <b-button
            variant="primary"
            @click="openAddPickupModal"
            :style="{
              opacity: selected.length === 0 ? '0.5' : '1',
              cursor: selected.length === 0 ? 'not-allowed' : 'pointer'
            }"
            id="call-courier-button"
          >
            {{ t('Call Courier') }}
            <b-tooltip
              triggers="hover"
              target="call-courier-button"
              :title="selected.length > 0 ? '' : t('Please select a shipment to call a courier.')"
              :delay="{ show: 0, hide: 0 }"
              boundary="viewport"
            />
          </b-button>
        </div>
      </div>
      <div>
        <vue-good-table
          mode="remote"
          :sort-options="{
            enabled: false
          }"
          :is-loading.sync="isLoading"
          :total-rows="totalRecords"
          :pagination-options="
            exportMode
              ? false
              : {
                  enabled: true,
                  jumpFirstOrLast: true,
                  rowsPerPageLabel: t('Rows per page'),
                  ofLabel: t('ofLabel'),
                  nextLabel: t('nextLabel'),
                  prevLabel: t('prevLabel'),
                  dropdownAllowAll: false
                }
          "
          :rows="rows"
          :columns="getColumns"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <table-no-result-found slot="emptystate" />
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === '#'">
              <template v-if="props.row.consignmentType === 0">
                <input
                  type="checkbox"
                  :checked="isSelected(props.row.barcode)"
                  @change="toggleSelection(props.row)"
                />
              </template>
              <template v-else> - </template>
            </span>
            <span v-else-if="props.column.field === 'barcode'">
              {{ props.row.barcode ? props.row.barcode : '-' }}
            </span>
            <span v-else-if="props.column.field === 'reference_no'">
              {{ props.row.reference_no ? props.row.reference_no : '-' }}
            </span>
            <span v-else-if="props.column.field === 'created_at'">
              {{ formatDateToCustom(props.row.created_at) }}
            </span>
            <span v-else-if="props.column.field === 'status_message'">
              {{ props.row.status_message ? props.row.status_message : '-' }}
            </span>
            <span v-else-if="props.column.field === 'consignor_city'">
              {{
                props.row.consignor && props.row.consignor.state ? props.row.consignor.state : '-'
              }}
            </span>
            <span v-else-if="props.column.field === 'consignee_country'">
              {{
                props.row.consignee && props.row.consignee.country
                  ? props.row.consignee.country
                  : '-'
              }}
            </span>
            <span v-else-if="props.column.field === 'consignee_city'">
              {{ props.row.consignee && props.row.consignee.city ? props.row.consignee.city : '-' }}
            </span>
            <span v-else-if="props.column.field === 'consignee_name'">
              {{ props.row.consignee && props.row.consignee.name ? props.row.consignee.name : '-' }}
            </span>
            <span v-else-if="props.column.field === 'total_cost'">
              {{ props.row.total_cost }} {{ getCurrencyCode(props.row.currency_code) }}
            </span>
            <span v-else-if="props.column.field === 'customer_name'">
              {{ props.row.customer && props.row.customer.name ? props.row.customer.name : '-' }}
            </span>
            <span v-else-if="props.column.field === 'consignee_state'">
              {{
                props.row.consignee && props.row.consignee.state ? props.row.consignee.state : '-'
              }}
            </span>
            <span v-else-if="props.column.field === 'consignor_name'">
              {{ props.row.consignor && props.row.consignor.name ? props.row.consignor.name : '-' }}
            </span>
            <span v-else-if="props.column.field === 'actions'">
              <span class="d-flex justify-content-center align-items-center gap-1">
                <b-dropdown variant="outline-primary" size="sm" toggle-class="btn-icon" no-caret>
                  <template #button-content>
                    <feather-icon icon="MoreHorizontalIcon" size="16" />
                  </template>

                  <b-dropdown-item :to="`/consignments/${props.row.id}`">
                    <feather-icon icon="FileTextIcon" size="16" class="mr-50 text-success" />
                    <span class="align-middle text-success">{{ t('Detail') }}</span>
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-if="props.row.status === 24"
                    @click="editConsignment(props.row.id)"
                  >
                    <feather-icon icon="Edit3Icon" size="16" class="mr-50" />
                    <span class="align-middle">{{ t('Edit Consignment') }}</span>
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-if="props.row.status === 1"
                    @click="cancelConsignment(props.row.id)"
                  >
                    <feather-icon icon="SlashIcon" size="16" class="mr-50 text-danger" />
                    <span class="align-middle text-danger">{{ t('Cancel') }}</span>
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-if="props.row.consignmentType === 2"
                    @click="formalizeShipmentConfirm(props.row.id)"
                  >
                    <feather-icon icon="SettingsIcon" size="16" class="mr-50" />
                    <span class="align-middle">{{ t('Formalize Shipment') }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </span>
            </span>
            <span v-else-if="props.column.field === 'provider_name'">
              <div class="d-flex align-items-center justify-content-center">
                <img
                  v-if="props.row.provider && getProviderLogo(props.row.provider)"
                  :src="getProviderLogo(props.row.provider)"
                  :alt="props.row.provider.name"
                  v-b-tooltip.hover
                  :title="props.row.provider.name"
                  style="height: 50px; object-fit: contain; max-width: 80px"
                />
                <span v-else>{{ props.row.provider ? props.row.provider.name : '-' }}</span>
              </div>
            </span>
          </template>
        </vue-good-table>
      </div>
      <AddPickupModal
        ref="callCourierForm"
        :title="t('Call Courier')"
        :address_book_list="address_book_list"
        :selected="selected"
      />
      <CustomColumnsModal
        ref="customColumnsModal"
        v-model="visibleColumnKeys"
        :options="columnToggleOptions"
        @save="saveCustomColumns"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BDropdown,
  BDropdownItem,
  BTooltip,
  BButton,
  VBTooltip
} from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { vueGoodTablePagination } from '@core/mixins/vueGoodTablePagination';
import { mapGetters } from 'vuex';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import ConsignmentFilter from '@/views/pages/consignments/ConsignmentFilter.vue';
import useService from '@/auth/service/useService';
import AddPickupModal from '@/components/AddPickupModal.vue';
import { getProviderLogo } from '@/libs/providerHelper';
import { checkHasRole } from '@core/utils/roleAccess';
import { ROLES, CUSTOMER_ROLES } from '@/constants/roles';
import CustomColumnsModal from './CustomColumnsModal.vue';

export default {
  components: {
    VueGoodTable,
    BCard,
    BCardBody,
    BDropdown,
    BDropdownItem,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ConsignmentFilter,
    AddPickupModal,
    CustomColumnsModal
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  mixins: [helper, vueGoodTablePagination],
  data() {
    return {
      isLoading: false,
      rows: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        companyId: '',
        orderId: '',
        dateFrom: '',
        dateTo: '',
        updateDateFrom: '',
        updateDateTo: '',
        bankId: '',
        type: '',
        status: [],
        consignmentType: '',
        provider: '',
        customer_codes: [],
        consigneeName: ''
      },
      selected: [],
      address_book_list: [],
      exportMode: null,
      visibleColumnKeys: [
        'reference_no',
        'created_at',
        'provider_name',
        'consignee_name',
        'consignee_state',
        'consignor_name',
        'total_cost'
      ]
    };
  },
  computed: {
    ...mapGetters({
      banks: 'app/banks',
      countries: 'app/countries',
      consignorStates: 'app/consignorStates',
      consignorCities: 'app/consignorCities'
    }),
    isAdmin() {
      return checkHasRole([ROLES.ADMIN, ROLES.ACCOUNT_ADMIN]);
    },
    isCustomer() {
      return checkHasRole(CUSTOMER_ROLES);
    },
    columnToggleOptions() {
      return [
        { text: this.$t('Barcode'), value: 'barcode', icon: 'HashIcon' },
        { text: this.$t('Reference Number'), value: 'reference_no', icon: 'FileTextIcon' },
        { text: this.$t('Provider'), value: 'provider_name', icon: 'TruckIcon' },
        { text: this.$t('Creation Date'), value: 'created_at', icon: 'CalendarIcon' },
        { text: this.$t('Status Message'), value: 'status_message', icon: 'ActivityIcon' },
        { text: this.$t('Consignor City'), value: 'consignor_city', icon: 'MapPinIcon' },
        { text: this.$t('Consignee Country'), value: 'consignee_country', icon: 'GlobeIcon' },
        { text: this.$t('Consignee City'), value: 'consignee_state', icon: 'MapIcon' },
        { text: this.$t('Consignee District'), value: 'consignee_city', icon: 'NavigationIcon' },
        { text: this.$t('Consignee Name Surname'), value: 'consignee_name', icon: 'UserIcon' },
        { text: this.$t('Fee Total'), value: 'total_cost', icon: 'DollarSignIcon' },
        { text: this.$t('Customer Name'), value: 'customer_name', icon: 'BriefcaseIcon' },
        { text: this.$t('Consignor Name'), value: 'consignor_name', icon: 'UserCheckIcon' }
      ];
    },
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      const baseColumns = [
        {
          label: '#',
          field: '#'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        },
        {
          label: this.$t('Provider'),
          field: 'provider_name',
          sortable: false
        },
        {
          label: this.$t('Customer Name'),
          field: 'customer_name'
        },
        {
          label: this.$t('Reference Number'),
          field: 'reference_no'
        },
        {
          label: this.$t('Creation Date'),
          field: 'created_at'
        },
        {
          label: this.$t('Consignee Name Surname'),
          field: 'consignee_name'
        },
        {
          label: this.$t('Consignee City'),
          field: 'consignee_state'
        },
        {
          label: this.$t('Consignor Name'),
          field: 'consignor_name'
        },
        {
          label: this.$t('Fee Total'),
          field: 'total_cost'
        },
        {
          label: this.$t('Barcode'),
          field: 'barcode'
        },
        {
          label: this.$t('Status Message'),
          field: 'status_message'
        },
        {
          label: this.$t('Consignor City'),
          field: 'consignor_city'
        },
        {
          label: this.$t('Consignee Country'),
          field: 'consignee_country'
        },
        {
          label: this.$t('Consignee District'),
          field: 'consignee_city'
        },
        {
          label: this.$t('Consignee District'),
          field: 'consignee_city'
        }
      ];
      // Filter columns according to visibility, but always keep selection and actions
      return baseColumns.filter(
        c => c.field === '#' || c.field === 'actions' || this.visibleColumnKeys.includes(c.field)
      );
    },
    isSelected() {
      return barcode => this.selected.some(item => item.value === barcode);
    }
  },
  watch: {
    visibleColumnKeys: {
      handler(newKeys) {
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(
              'consignments-visible-columns',
              JSON.stringify(newKeys || [])
            );
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      },
      deep: false
    }
  },
  async created() {
    const appliedFilters = this.getAppliedFilters();
    if (appliedFilters) {
      const { page, limit, ...args } = appliedFilters;
      this.filterValues = {
        ...this.filterValues,
        ...args
      };

      this.filter = {
        page: page || 1,
        limit: limit || 10
      };
    }

    // Load saved visible columns from localStorage
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedColumns = window.localStorage.getItem('consignments-visible-columns');
        if (savedColumns) {
          const parsed = JSON.parse(savedColumns);
          if (Array.isArray(parsed) && parsed.length) {
            this.visibleColumnKeys = parsed;
          }
        }
      }
    } catch (e) {
      // ignore JSON or storage errors
    }

    this.loadItems();
    await this.getAllAddressBookList();
    await this.$store.dispatch('app/getCountries');
    await this.$store.dispatch('app/getCurrencies');
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },
    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    async loadItems() {
      const requestData = {
        ...this.filter,
        ...this.filterValues
      };
      if (
        requestData.dateFrom &&
        requestData.dateTo &&
        new Date(requestData.dateFrom) > new Date(requestData.dateTo)
      ) {
        this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'));
        return;
      }
      if (requestData.status && requestData.status.length) {
        requestData.status = requestData.status.map(i => i).join(',');
      }
      if (requestData.customer_codes && requestData.customer_codes.length) {
        if (requestData.customer_codes.includes('ALL')) {
          delete requestData.customer_codes;
        } else {
          requestData.customer_codes = requestData.customer_codes.join(',');
        }
      } else {
        delete requestData.customer_codes;
      }
      this.isLoading = true;

      try {
        const response = await useService.getAllConsigments(requestData);
        this.rows = response.data;
        this.totalRecords = response.meta.total;
      } catch (error) {
        this.showErrorMessage(error);
      } finally {
        this.isLoading = false;
      }
    },
    setFilter(data) {
      this.filterValues = data;
      this.filter.page = 1;
      // Persist filters (including paging) so they survive navigation
      this.applyFilters({
        ...this.filter,
        ...this.filterValues
      });
      this.loadItems();
    },
    toggleSelection(row) {
      const index = this.selected.findIndex(item => item.value === row.barcode);

      if (index === -1) {
        this.selected.push({
          type: 'barcode',
          value: row.barcode
        });
      } else {
        this.selected.splice(index, 1);
      }
    },
    getAllAddressBookList() {
      useService
        .getAllWarehouses({
          page: 1,
          limit: 1000
        })
        .then(response => {
          this.address_book_list = response.data.map(item => {
            return {
              ...item,
              text: item.name,
              value: item.id
            };
          });
        });
    },
    openAddPickupModal() {
      if (this.selected.length > 0) {
        this.$bvModal.show('add-pickup-modal');

        this.$refs.callCourierForm.clearEditedId();
      }
    },
    openCustomColumnsModal() {
      this.$refs.customColumnsModal.show();
    },
    saveCustomColumns(newColumns) {
      this.visibleColumnKeys = newColumns;
    },
    openExportFromToolbar() {
      const { dateFrom, dateTo, dropOffDateFrom, dropOffDateTo } = this.filterValues;

      if ((dateFrom && !dateTo) || (dropOffDateFrom && !dropOffDateTo)) {
        return this.showErrorMessage(this.$t('Please select an end date'));
      }

      if (!(dateFrom && dateTo) && !(dropOffDateFrom && dropOffDateTo)) {
        return this.showErrorMessage(this.$t('Please select a start date'));
      }

      this.exportMode = 'customer';
      this.$bvModal.show('downloadModal');
    },
    openAdminExportFromToolbar() {
      const { dateFrom, dateTo, dropOffDateFrom, dropOffDateTo } = this.filterValues;

      if ((dateFrom && !dateTo) || (dropOffDateFrom && !dropOffDateTo)) {
        return this.showErrorMessage(this.$t('Please select an end date'));
      }

      if (!(dateFrom && dateTo) && !(dropOffDateFrom && dropOffDateTo)) {
        return this.showErrorMessage(this.$t('Please select a start date'));
      }

      this.exportMode = 'admin';
      this.$bvModal.show('downloadModal');
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t,
      getProviderLogo
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.table-responsive {
  .vgt-responsive {
    overflow: auto;
  }
}

.custom-pagination-wrapper {
  .page-input-group {
    .page-input {
      width: 60px;
      text-align: center;
    }
  }

  .current-page-dropdown {
    min-width: 50px;
  }

  .btn.active {
    background-color: #007bff;
    border-color: #007bff;
    color: #fff;
  }
}

.columns-dropdown-wrapper {
  position: relative;

  ::v-deep .dropdown-menu {
    position: absolute !important;
    top: 100% !important;
    left: auto !important;
    right: 0 !important;
    transform: none !important;
    will-change: auto !important;
  }
}

.columns-dropdown-menu {
  .custom-control {
    margin-bottom: 0.55rem;
  }

  .custom-control-label {
    line-height: 1.5;
  }
}

.action-button-detail,
.action-button-cancel {
  border-radius: 6px;
  padding: 6px 16px;
  font-weight: 500;
  font-size: 0.875rem;
  min-width: 80px;
  margin-left: 4px;
}

.action-button-detail {
  background-color: #00a0df;
  border-color: #00a0df;
  color: #ffffff;
}

.action-button-detail:hover {
  background-color: #0088c7;
  border-color: #0088c7;
}

.action-button-cancel {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #ffffff;
}

.action-button-cancel:hover {
  background-color: #c82333;
  border-color: #bd2130;
}
</style>
